import React, { useState, useRef, useCallback } from 'react';
import { Plus, Trash2, Grid3X3, Copy } from 'lucide-react';

/**
 * PriceMatrixEditor — 2D price lookup table editor with bilinear interpolation.
 * Stored inside spreadsheet.matrices JSONB. Used in formulas as MATRIX(id, row, col).
 */
export default function PriceMatrixEditor({ matrices = {}, onChange, readOnly }) {
  const [activeId, setActiveId] = useState(() => Object.keys(matrices)[0] || null);
  const matrix = activeId ? matrices[activeId] : null;
  const tableRef = useRef(null);

  const update = useCallback((id, newMatrix) => {
    onChange({ ...matrices, [id]: newMatrix });
  }, [matrices, onChange]);

  const addMatrix = () => {
    const ids = Object.keys(matrices);
    const nextId = `m${ids.length + 1}`;
    const newMatrix = {
      name: `Matryca ${ids.length + 1}`,
      rowAxis: { label: 'Szerokość (cm)', values: [50, 100, 150, 200] },
      colAxis: { label: 'Wysokość (cm)', values: [50, 100, 150, 200] },
      data: [[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]],
      interpolation: 'bilinear'
    };
    onChange({ ...matrices, [nextId]: newMatrix });
    setActiveId(nextId);
  };

  const deleteMatrix = (id) => {
    const copy = { ...matrices };
    delete copy[id];
    onChange(copy);
    setActiveId(Object.keys(copy)[0] || null);
  };

  const updateCell = (ri, ci, value) => {
    if (!matrix) return;
    const newData = matrix.data.map((r, i) => i === ri ? r.map((c, j) => j === ci ? (parseFloat(value) || 0) : c) : [...r]);
    update(activeId, { ...matrix, data: newData });
  };

  const updateRowAxisValue = (idx, value) => {
    if (!matrix) return;
    const newValues = [...matrix.rowAxis.values];
    newValues[idx] = parseFloat(value) || 0;
    update(activeId, { ...matrix, rowAxis: { ...matrix.rowAxis, values: newValues } });
  };

  const updateColAxisValue = (idx, value) => {
    if (!matrix) return;
    const newValues = [...matrix.colAxis.values];
    newValues[idx] = parseFloat(value) || 0;
    update(activeId, { ...matrix, colAxis: { ...matrix.colAxis, values: newValues } });
  };

  const addRow = () => {
    if (!matrix) return;
    const lastVal = matrix.rowAxis.values[matrix.rowAxis.values.length - 1] || 0;
    const step = matrix.rowAxis.values.length > 1 ? matrix.rowAxis.values[matrix.rowAxis.values.length - 1] - matrix.rowAxis.values[matrix.rowAxis.values.length - 2] : 50;
    const newRowValues = [...matrix.rowAxis.values, lastVal + step];
    const newData = [...matrix.data, new Array(matrix.colAxis.values.length).fill(0)];
    update(activeId, { ...matrix, rowAxis: { ...matrix.rowAxis, values: newRowValues }, data: newData });
  };

  const addCol = () => {
    if (!matrix) return;
    const lastVal = matrix.colAxis.values[matrix.colAxis.values.length - 1] || 0;
    const step = matrix.colAxis.values.length > 1 ? matrix.colAxis.values[matrix.colAxis.values.length - 1] - matrix.colAxis.values[matrix.colAxis.values.length - 2] : 50;
    const newColValues = [...matrix.colAxis.values, lastVal + step];
    const newData = matrix.data.map(r => [...r, 0]);
    update(activeId, { ...matrix, colAxis: { ...matrix.colAxis, values: newColValues }, data: newData });
  };

  const removeRow = (idx) => {
    if (!matrix || matrix.rowAxis.values.length <= 2) return;
    const newRowValues = matrix.rowAxis.values.filter((_, i) => i !== idx);
    const newData = matrix.data.filter((_, i) => i !== idx);
    update(activeId, { ...matrix, rowAxis: { ...matrix.rowAxis, values: newRowValues }, data: newData });
  };

  const removeCol = (idx) => {
    if (!matrix || matrix.colAxis.values.length <= 2) return;
    const newColValues = matrix.colAxis.values.filter((_, i) => i !== idx);
    const newData = matrix.data.map(r => r.filter((_, i) => i !== idx));
    update(activeId, { ...matrix, colAxis: { ...matrix.colAxis, values: newColValues }, data: newData });
  };

  // Paste from clipboard (tab-separated)
  const handlePaste = useCallback((e) => {
    if (!matrix || readOnly) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    const rows = text.trim().split('\n').map(r => r.split('\t').map(c => parseFloat(c.replace(',', '.').replace(/\s/g, '')) || 0));
    if (rows.length === 0) return;
    e.preventDefault();

    // Detect if first row/col are axis values
    const hasHeaders = rows[0].length === matrix.colAxis.values.length + 1 && rows.length === matrix.rowAxis.values.length + 1;

    if (hasHeaders) {
      const newColValues = rows[0].slice(1);
      const newRowValues = rows.slice(1).map(r => r[0]);
      const newData = rows.slice(1).map(r => r.slice(1));
      update(activeId, {
        ...matrix,
        rowAxis: { ...matrix.rowAxis, values: newRowValues },
        colAxis: { ...matrix.colAxis, values: newColValues },
        data: newData
      });
    } else {
      // Just paste data into existing grid
      const newData = matrix.data.map((r, ri) =>
        r.map((c, ci) => (rows[ri] && rows[ri][ci] !== undefined) ? rows[ri][ci] : c)
      );
      // Expand grid if pasted data is larger
      if (rows.length > matrix.data.length || (rows[0] && rows[0].length > matrix.data[0].length)) {
        const maxRows = Math.max(matrix.data.length, rows.length);
        const maxCols = Math.max(matrix.data[0]?.length || 0, rows[0]?.length || 0);
        const expandedData = [];
        const expandedRowValues = [...matrix.rowAxis.values];
        const expandedColValues = [...matrix.colAxis.values];
        const step = matrix.rowAxis.values.length > 1 ? matrix.rowAxis.values[1] - matrix.rowAxis.values[0] : 50;
        const cStep = matrix.colAxis.values.length > 1 ? matrix.colAxis.values[1] - matrix.colAxis.values[0] : 50;
        while (expandedRowValues.length < maxRows) expandedRowValues.push(expandedRowValues[expandedRowValues.length - 1] + step);
        while (expandedColValues.length < maxCols) expandedColValues.push(expandedColValues[expandedColValues.length - 1] + cStep);
        for (let ri = 0; ri < maxRows; ri++) {
          expandedData[ri] = [];
          for (let ci = 0; ci < maxCols; ci++) {
            expandedData[ri][ci] = (rows[ri] && rows[ri][ci] !== undefined) ? rows[ri][ci] : (matrix.data[ri]?.[ci] || 0);
          }
        }
        update(activeId, {
          ...matrix,
          rowAxis: { ...matrix.rowAxis, values: expandedRowValues },
          colAxis: { ...matrix.colAxis, values: expandedColValues },
          data: expandedData
        });
        return;
      }
      update(activeId, { ...matrix, data: newData });
    }
  }, [matrix, activeId, update, readOnly]);

  const ids = Object.keys(matrices);

  // Color scale for cells
  const getColor = (val, min, max) => {
    if (max === min) return 'bg-blue-50';
    const ratio = (val - min) / (max - min);
    if (ratio < 0.25) return 'bg-blue-50';
    if (ratio < 0.5) return 'bg-blue-100';
    if (ratio < 0.75) return 'bg-orange-50';
    return 'bg-orange-100';
  };

  let dataMin = 0, dataMax = 0;
  if (matrix?.data) {
    const flat = matrix.data.flat().filter(v => v > 0);
    dataMin = Math.min(...flat);
    dataMax = Math.max(...flat);
  }

  return (
    <div className="border border-indigo-200 rounded-lg bg-indigo-50/30 mt-2">
      {/* Tabs + controls */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-indigo-200 bg-indigo-50">
        <Grid3X3 size={14} className="text-indigo-500" />
        <span className="text-xs font-medium text-indigo-700 mr-2">Matryce cenowe</span>
        {ids.map(id => (
          <button key={id} onClick={() => setActiveId(id)}
            className={`px-2 py-0.5 rounded text-xs font-mono ${activeId === id ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600 hover:bg-indigo-100'}`}>
            {id}
          </button>
        ))}
        {!readOnly && (
          <button onClick={addMatrix} className="px-1.5 py-0.5 text-xs text-indigo-500 hover:bg-indigo-100 rounded">
            <Plus size={12} className="inline" /> Dodaj
          </button>
        )}
        {activeId && (
          <span className="ml-auto text-[10px] text-indigo-400 font-mono">
            =MATRIX({activeId}, szer, wys)
          </span>
        )}
      </div>

      {matrix && (
        <div className="p-2 overflow-auto" ref={tableRef} onPaste={handlePaste}>
          {/* Axis labels */}
          <div className="flex items-center gap-4 mb-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Wiersze:</span>
              {!readOnly ? (
                <input value={matrix.rowAxis.label} onChange={e => update(activeId, { ...matrix, rowAxis: { ...matrix.rowAxis, label: e.target.value } })}
                  className="px-1 py-0.5 border border-gray-300 rounded text-xs w-32" />
              ) : <span className="text-gray-700">{matrix.rowAxis.label}</span>}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Kolumny:</span>
              {!readOnly ? (
                <input value={matrix.colAxis.label} onChange={e => update(activeId, { ...matrix, colAxis: { ...matrix.colAxis, label: e.target.value } })}
                  className="px-1 py-0.5 border border-gray-300 rounded text-xs w-32" />
              ) : <span className="text-gray-700">{matrix.colAxis.label}</span>}
            </div>
            {!readOnly && (
              <button onClick={() => deleteMatrix(activeId)} className="ml-auto text-red-400 hover:text-red-600 text-xs flex items-center gap-0.5">
                <Trash2 size={11} /> Usuń matrycę
              </button>
            )}
          </div>

          {/* Matrix table */}
          <div className="overflow-auto max-h-[400px]">
            <table className="border-collapse text-xs">
              <thead>
                <tr>
                  <th className="p-1 bg-gray-100 border border-gray-300 text-gray-500 text-[10px] sticky left-0 z-10">
                    {matrix.rowAxis.label?.split('(')[0]?.trim() || '↓'} \ {matrix.colAxis.label?.split('(')[0]?.trim() || '→'}
                  </th>
                  {matrix.colAxis.values.map((cv, ci) => (
                    <th key={ci} className="p-0.5 bg-blue-50 border border-gray-300 min-w-[60px]">
                      {!readOnly ? (
                        <div className="flex items-center">
                          <input value={cv} onChange={e => updateColAxisValue(ci, e.target.value)}
                            className="w-full text-center text-xs font-medium bg-transparent border-none outline-none" />
                          {matrix.colAxis.values.length > 2 && (
                            <button onClick={() => removeCol(ci)} className="text-red-300 hover:text-red-500 flex-shrink-0"><Trash2 size={9} /></button>
                          )}
                        </div>
                      ) : <span className="font-medium">{cv}</span>}
                    </th>
                  ))}
                  {!readOnly && (
                    <th className="p-0.5 border border-gray-300 bg-gray-50">
                      <button onClick={addCol} className="text-blue-400 hover:text-blue-600"><Plus size={12} /></button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {matrix.data.map((row, ri) => (
                  <tr key={ri}>
                    <td className="p-0.5 bg-emerald-50 border border-gray-300 font-medium text-center sticky left-0 z-10 min-w-[60px]">
                      {!readOnly ? (
                        <div className="flex items-center">
                          <input value={matrix.rowAxis.values[ri]} onChange={e => updateRowAxisValue(ri, e.target.value)}
                            className="w-full text-center text-xs font-medium bg-transparent border-none outline-none" />
                          {matrix.rowAxis.values.length > 2 && (
                            <button onClick={() => removeRow(ri)} className="text-red-300 hover:text-red-500 flex-shrink-0"><Trash2 size={9} /></button>
                          )}
                        </div>
                      ) : <span>{matrix.rowAxis.values[ri]}</span>}
                    </td>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`p-0.5 border border-gray-300 text-right ${getColor(cell, dataMin, dataMax)}`}>
                        {!readOnly ? (
                          <input value={cell} onChange={e => updateCell(ri, ci, e.target.value)}
                            className="w-full text-right text-xs bg-transparent border-none outline-none tabular-nums"
                            onFocus={e => e.target.select()} />
                        ) : (
                          <span className="tabular-nums">{cell.toLocaleString('pl-PL')}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {!readOnly && (
                  <tr>
                    <td className="p-0.5 border border-gray-300 bg-gray-50 text-center">
                      <button onClick={addRow} className="text-emerald-400 hover:text-emerald-600"><Plus size={12} /></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Info */}
          <div className="mt-2 text-[10px] text-gray-400">
            Użyj w formule: <code className="bg-gray-100 px-1 rounded font-mono">=MATRIX({activeId}, l_szerokosc, l_wysokosc)</code>
            {' '}— interpolacja biliniowa między punktami siatki. Ctrl+V wkleja dane z Excela.
          </div>
        </div>
      )}

      {!matrix && ids.length === 0 && !readOnly && (
        <div className="p-4 text-center text-xs text-gray-400">
          <button onClick={addMatrix} className="px-3 py-1.5 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200">
            <Grid3X3 size={14} className="inline mr-1" /> Utwórz matrycę cenową
          </button>
        </div>
      )}
    </div>
  );
}
